import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Button, Dropdown, Radio, Divider } from "antd"
import { DownOutlined } from "@ant-design/icons"
import { setSort } from "store/trips"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import styles from "./TripsSort.module.css"

const TripsSortMenu = ({
  priceSort,
  distanceSort,
  onPriceChange,
  onDistanceChange,
  onReset,
  onConfirm,
  isResetDisabled,
  isConfirmDisabled
}) => {
  const { t } = useTranslation()

  return (
    <div className={styles.tripsSortMenu}>
      <div className={styles.tripsSortTitle}>{t("tripsSort.sort_by")}</div>
      <div className={styles.tripsSortSection}>{t("tripsSort.price")}</div>
      <Radio.Group value={priceSort} onChange={onPriceChange} className={styles.tripsSortRadioGroup}>
        <Radio value="asc">{t("tripsSort.low_to_high")}</Radio>
        <Radio value="desc">{t("tripsSort.high_to_low")}</Radio>
      </Radio.Group>
      <Divider className={styles.tripsSortDivider} />
      <div className={styles.tripsSortSection}>{t("tripsSort.distance")}</div>
      <Radio.Group value={distanceSort} onChange={onDistanceChange} className={styles.tripsSortRadioGroup}>
        <Radio value="asc">{t("tripsSort.nearest_first")}</Radio>
        <Radio value="desc">{t("tripsSort.farthest_first")}</Radio>
      </Radio.Group>
      <div className={styles.tripsSortActions}>
        <Button type="link" onClick={onReset} disabled={isResetDisabled}>
          {t("tripsSort.reset_sorting")}
        </Button>
        <Button type="primary" onClick={onConfirm} disabled={isConfirmDisabled}>
          {t("tripsSort.confirm")}
        </Button>
      </div>
    </div>
  )
}

TripsSortMenu.propTypes = {
  priceSort: PropTypes.string.isRequired,
  distanceSort: PropTypes.string.isRequired,
  onPriceChange: PropTypes.func.isRequired,
  onDistanceChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isResetDisabled: PropTypes.bool.isRequired,
  isConfirmDisabled: PropTypes.bool.isRequired
}

export default function TripsSort() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const sort = useSelector((state) => state.trips.sort)

  const [open, setOpen] = useState(false)
  const [priceSort, setPriceSort] = useState(sort.price_sort || "")
  const [distanceSort, setDistanceSort] = useState(sort.distance_sort || "asc")

  useEffect(() => {
    setPriceSort(sort.price_sort || "")
    setDistanceSort(sort.distance_sort || "asc")
  }, [sort.price_sort, sort.distance_sort])

  const handlePriceChange = (e) => setPriceSort(e.target.value)
  const handleDistanceChange = (e) => setDistanceSort(e.target.value)
  const handleReset = () => {
    setPriceSort("")
    setDistanceSort("asc")
  }
  const handleConfirm = () => {
    dispatch(setSort({ price_sort: priceSort, distance_sort: distanceSort }))
    setOpen(false)
  }

  let label = t("tripsSort.sort_by_distance_nearest")
  if (priceSort) label = t("tripsSort.sort_by_price", { order: t(priceSort === "asc" ? "tripsSort.low_to_high" : "tripsSort.high_to_low") })
  else if (distanceSort === "desc") label = t("tripsSort.sort_by_distance_farthest")

  const isResetDisabled = !priceSort && distanceSort === "asc"
  const isConfirmDisabled = sort.price_sort === priceSort && sort.distance_sort === distanceSort

  const renderDropdownContent = () => (
    <TripsSortMenu
      priceSort={priceSort}
      distanceSort={distanceSort}
      onPriceChange={handlePriceChange}
      onDistanceChange={handleDistanceChange}
      onReset={handleReset}
      onConfirm={handleConfirm}
      isResetDisabled={isResetDisabled}
      isConfirmDisabled={isConfirmDisabled}
    />
  )

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      dropdownRender={renderDropdownContent}
      trigger={['click']}
      placement="bottomLeft"
    >
      <Button color="primary" variant="filled" shape="round" style={{ borderColor: "var(--primary-color)" }}>
        {label} <DownOutlined />
      </Button>
    </Dropdown>
  )
}